import { useRef, useState } from "react";
import { ApiError, resendTwoFactor } from "@/api/client";
import { AccountLoadError, useSession } from "@/auth/session";
import { useCountdown } from "@/features/auth/useCountdown";

const RESEND_COOLDOWN = 60;
const COULD_NOT_SEND = "We couldn't send your sign-in code. Try again shortly.";
const TOO_MANY = "Too many attempts. Try again shortly.";
const EXPIRED = "This code has expired. Sign in again.";
const CODE_LIMIT = "You can't request more codes right now. Sign in again if you need a new one.";
const ACCOUNT_LOAD = "You're signed in, but we couldn't load your account. Reload the page.";

const inputClass =
  "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-zinc-950 shadow-sm outline-none transition placeholder:text-zinc-400 focus:border-brand focus:ring-2 focus:ring-brand/20";
const primaryClass =
  "w-full min-h-11 rounded-md bg-brand-strong px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-stronger focus:outline-none focus:ring-2 focus:ring-brand/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-500 disabled:shadow-none";
const secondaryClass =
  "w-full min-h-11 rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-sm font-medium text-zinc-700 transition hover:border-brand/60 hover:bg-brand-light/10 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400";

type Pending = { challenge: string; emailHint: string };

// Signing in changes the session and the route's beforeLoad sends the user on to their target.
export function LoginPage() {
  const { signIn } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  // Set only when coming back from the code step, so a fresh page load keeps the browser's focus.
  const [returnFocus, setReturnFocus] = useState<"email" | "password" | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const challenge = await signIn(email, password);
      if (challenge) setPending({ challenge: challenge.challenge, emailHint: challenge.email_hint });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? TOO_MANY
          : err instanceof ApiError && err.status === 503
            ? COULD_NOT_SEND
            : "Incorrect email or password.",
      );
    } finally {
      setBusy(false);
    }
  }

  function backToPassword(message: string | null, keepEmail: boolean) {
    setPending(null);
    setPassword("");
    if (!keepEmail) setEmail("");
    setError(message);
    setReturnFocus(keepEmail ? "password" : "email");
  }

  return (
    <div className="min-h-dvh bg-zinc-50 text-zinc-950">
      <div className="grid min-h-dvh px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_28rem] lg:p-0">
        <section className="hidden bg-brand-ink text-white lg:flex lg:flex-col lg:justify-between lg:px-12 lg:py-10">
          <div className="flex items-center gap-3">
            <img src="/cipm-logo.png" alt="" className="h-11 w-11 rounded-full bg-white p-1" />
            <span className="text-sm font-semibold uppercase tracking-[0.16em] text-white/75">CIPM Nigeria</span>
          </div>
          <div className="max-w-lg pb-10">
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-brand-light">Certificate operations</p>
            <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight">Controlled access for issuing and verifying professional certificates.</h1>
            <p className="mt-5 max-w-md text-base leading-7 text-white/72">
              Manage member records, approvals, print runs, delivery status, and audit history from one secure workspace.
            </p>
          </div>
          <p className="text-xs text-white/55">Authorized staff access only</p>
        </section>

        <main className="grid place-items-center lg:bg-white">
          <div className="w-full max-w-sm space-y-6 rounded-lg border border-zinc-200 bg-white p-6 shadow-sm sm:p-8 lg:border-0 lg:shadow-none">
            <div>
              <img src="/cipm-logo.png" alt="CIPM logo" className="mb-5 h-14 w-14 lg:hidden" />
              <p className="text-sm font-medium text-brand-ink">CIPM Certificate Platform</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">Sign in</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-600">Use your staff credentials to continue.</p>
            </div>

            {pending ? (
              <CodeStep
                {...pending}
                onExpired={() => backToPassword(EXPIRED, true)}
                onDifferentAccount={() => backToPassword(null, false)}
              />
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <label className="block text-sm font-medium">
                  <span className="text-zinc-700">Email</span>
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    autoFocus={returnFocus === "email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                  />
                </label>

                <label className="block text-sm font-medium">
                  <span className="text-zinc-700">Password</span>
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    autoFocus={returnFocus === "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                </label>

                {error && (
                  <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={busy} className={primaryClass}>
                  {busy ? "Signing in..." : "Sign in"}
                </button>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function CodeStep({
  challenge,
  emailHint,
  onExpired,
  onDifferentAccount,
}: Pending & { onExpired: () => void; onDifferentAccount: () => void }) {
  const { completeTwoFactor } = useSession();
  const { remaining, restart } = useCountdown(RESEND_COOLDOWN);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await completeTwoFactor(challenge, code, remember);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && err.code === "code_invalid") {
        setError(wrongCodeMessage(err));
        setCode("");
        codeRef.current?.focus();
      } else if (err instanceof ApiError && err.status === 401) {
        onExpired();
        return;
      } else if (err instanceof AccountLoadError) {
        setError(ACCOUNT_LOAD);
      } else {
        setError(err instanceof ApiError && err.status === 429 ? TOO_MANY : "Something went wrong. Try again.");
      }
    }
    setBusy(false);
  }

  async function resend() {
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      await resendTwoFactor(challenge);
      setNotice("We sent a new code.");
      restart(RESEND_COOLDOWN);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onExpired();
        return;
      }
      // ApiError carries no headers, so Retry-After is unknown; fall back to the full cooldown.
      if (err instanceof ApiError && err.status === 429) {
        setError(CODE_LIMIT);
        restart(RESEND_COOLDOWN);
      } else setError(err instanceof ApiError && err.status === 503 ? COULD_NOT_SEND : "Something went wrong. Try again.");
    }
    setResending(false);
  }

  return (
    <form onSubmit={verify} className="space-y-5">
      <p className="text-sm leading-6 text-zinc-700">We sent a 6-digit code to {emailHint}.</p>

      <label className="block text-sm font-medium">
        <span className="text-zinc-700">Sign-in code</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          ref={codeRef}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className={`${inputClass} tracking-widest`}
        />
      </label>

      <label className="flex items-center gap-2 text-sm text-zinc-700">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-300 accent-brand-strong"
        />
        Remember this device for 30 days
      </label>

      {error && (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {notice}
        </p>
      )}

      <button type="submit" disabled={busy || code.length !== 6} className={primaryClass}>
        {busy ? "Verifying…" : "Verify"}
      </button>

      <div className="space-y-2">
        <button type="button" onClick={resend} disabled={busy || resending || remaining > 0} className={secondaryClass}>
          {remaining > 0 ? `Send a new code (${remaining}s)` : "Send a new code"}
        </button>
        <button type="button" onClick={onDifferentAccount} disabled={busy} className={secondaryClass}>
          Use a different account
        </button>
      </div>
    </form>
  );
}

function wrongCodeMessage(err: ApiError): string {
  const issue = err.details.find((d) => d.issue?.startsWith("attempts_left:"))?.issue;
  const left = issue ? Number(issue.slice("attempts_left:".length)) : NaN;
  if (!Number.isFinite(left)) return "That code isn't right.";
  return `That code isn't right. ${left} ${left === 1 ? "attempt" : "attempts"} left.`;
}
